export const MAPPING_SCHEMA = 'template-import-mapping/v1';
export const CORRECTION_SCHEMA = 'template-import-correction/v1';
export const F3_D8_TOKEN = '+-';
export const GOVERNED_SOURCE_FAMILY_F3 = 'F3';

export const ASSESSMENTS = ['PASS', 'FAIL', 'NOT_APPLICABLE'] as const;
export type CanonicalAssessment = (typeof ASSESSMENTS)[number];

export const COLUMN_NAMES = [
  'section_code',
  'section_title',
  'item_code',
  'item_label',
  'item_ordinal',
  'assessment_token',
] as const;
export type ColumnName = (typeof COLUMN_NAMES)[number];

export const BINDING_KINDS = ['CASE_SNAPSHOT', 'CHECKLIST_DYNAMIC'] as const;
export const VALUE_TYPES = [
  'STRING',
  'TEXT',
  'NUMBER',
  'DATE',
  'DATETIME',
  'ENUM',
  'REF_CONTRACTOR',
  'REF_VEHICLE',
  'REF_PERSON',
] as const;
export const OWNER_CODES = ['CONTRACTOR', 'INSPECTOR', 'SYSTEM'] as const;
export const PLACEMENTS = ['HEADER', 'OBJECT_BLOCK', 'RESULT_BLOCK', 'SIGNATURE_BLOCK'] as const;
export const FAIL_EVIDENCE_REQUIREMENTS = ['nonconformity_description', 'comment'] as const;
export const FORMULA_CELL_POLICIES = ['error', 'literal_formula_text'] as const;

export type FormulaCellPolicy = (typeof FORMULA_CELL_POLICIES)[number];

export type ColumnBinding =
  | { readonly kind: 'column'; readonly column: string }
  | { readonly kind: 'fixed'; readonly value: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'source_order' };

export interface FieldMapping {
  readonly field_key: string;
  readonly binding_kind: (typeof BINDING_KINDS)[number];
  readonly label: string;
  readonly value_type: (typeof VALUE_TYPES)[number];
  readonly owner_code: (typeof OWNER_CODES)[number];
  readonly required_rule: Record<string, unknown>;
  readonly applicability_rule: Record<string, unknown>;
  readonly validation: Record<string, unknown>;
  readonly placement: (typeof PLACEMENTS)[number];
  readonly pdf_metadata: Record<string, unknown>;
  readonly snapshot_path: string | null;
  readonly presentation_only?: boolean;
}

export interface FailEvidenceRule {
  readonly assessment: 'FAIL';
  readonly require: readonly (typeof FAIL_EVIDENCE_REQUIREMENTS)[number][];
}

export interface DeclarativeMapping {
  readonly schema: typeof MAPPING_SCHEMA;
  readonly header_row: number;
  readonly data_start_row: number;
  readonly formula_cells: FormulaCellPolicy;
  readonly columns: Record<ColumnName, ColumnBinding>;
  readonly assessment_tokens: {
    readonly PASS: readonly string[];
    readonly FAIL: readonly string[];
    readonly NOT_APPLICABLE: readonly string[];
  };
  readonly fail_evidence: Readonly<Record<string, FailEvidenceRule>>;
  readonly fields: readonly FieldMapping[];
}

export interface ItemCodeRewrite {
  readonly from: string;
  readonly to: string;
}

export interface CorrectionOverlay {
  readonly assessment_tokens?: {
    readonly PASS?: readonly string[];
    readonly FAIL?: readonly string[];
    readonly NOT_APPLICABLE?: readonly string[];
  };
  readonly fail_evidence?: DeclarativeMapping['fail_evidence'];
  readonly columns?: Partial<DeclarativeMapping['columns']>;
  readonly fields?: readonly FieldMapping[];
  readonly item_code_rewrites?: readonly ItemCodeRewrite[];
  readonly formula_cells?: FormulaCellPolicy;
}

export interface MappingProblem {
  readonly code: string;
  readonly message: string;
}

const EMPTY_OBJECT: Record<string, unknown> = {};

export function emptyRuleObject(): Record<string, unknown> {
  return {};
}

export function defaultGenericMapping(): DeclarativeMapping {
  return {
    schema: MAPPING_SCHEMA,
    header_row: 1,
    data_start_row: 2,
    formula_cells: 'error',
    columns: {
      section_code: { kind: 'absent' },
      section_title: { kind: 'absent' },
      item_code: { kind: 'column', column: 'A' },
      item_label: { kind: 'column', column: 'B' },
      item_ordinal: { kind: 'source_order' },
      assessment_token: { kind: 'absent' },
    },
    assessment_tokens: {
      PASS: [],
      FAIL: [],
      NOT_APPLICABLE: [],
    },
    fail_evidence: {},
    fields: [],
  };
}

export function defaultF3Mapping(): DeclarativeMapping {
  return {
    ...defaultGenericMapping(),
    columns: {
      ...defaultGenericMapping().columns,
      assessment_token: { kind: 'column', column: 'C' },
    },
    assessment_tokens: {
      PASS: ['+', 'PASS'],
      FAIL: ['-', 'FAIL', F3_D8_TOKEN],
      NOT_APPLICABLE: ['N/A', 'NA'],
    },
    fail_evidence: {
      [F3_D8_TOKEN]: {
        assessment: 'FAIL',
        require: ['nonconformity_description', 'comment'],
      },
    },
  };
}

export function defaultMappingForFamily(sourceFamily: string): DeclarativeMapping {
  return sourceFamily === GOVERNED_SOURCE_FAMILY_F3 ? defaultF3Mapping() : defaultGenericMapping();
}

export const MAPPING_SCHEMA_V2 = 'template-import-mapping/v2';

export const ITEM_CODE_MODES_V2 = ['HIERARCHICAL_NUMERIC', 'INTEGER', 'GENERATED'] as const;
export type ItemCodeModeV2 = (typeof ITEM_CODE_MODES_V2)[number];

export interface HeaderAliasesV2 {
  readonly item_code: readonly string[];
  readonly item_label: readonly string[];
  readonly normative_reference: readonly string[];
  readonly pass: readonly string[];
  readonly fail: readonly string[];
  readonly not_applicable: readonly string[];
  readonly comment: readonly string[];
}

export interface DiscoveryGrammarV2 {
  readonly scan_from_row: number;
  readonly scan_to_row: number;
  readonly allow_multiple_regions: boolean;
  readonly max_consecutive_non_item_rows: number;
  readonly preceding_section_title_rows: number;
  readonly header_aliases: HeaderAliasesV2;
  readonly item_code_modes: readonly ItemCodeModeV2[];
}

export interface DeclarativeMappingV2 {
  readonly schema: typeof MAPPING_SCHEMA_V2;
  readonly formula_cells: FormulaCellPolicy;
  readonly discovery: DiscoveryGrammarV2;
  readonly assessment_tokens: DeclarativeMapping['assessment_tokens'];
  readonly fail_evidence: DeclarativeMapping['fail_evidence'];
  readonly fields: readonly FieldMapping[];
}

export type AnyDeclarativeMapping = DeclarativeMapping | DeclarativeMappingV2;

const HEADER_ALIAS_KEYS_V2 = [
  'item_code',
  'item_label',
  'normative_reference',
  'pass',
  'fail',
  'not_applicable',
  'comment',
] as const;

export function isDeclarativeMappingV2(
  mapping: AnyDeclarativeMapping,
): mapping is DeclarativeMappingV2 {
  return mapping.schema === MAPPING_SCHEMA_V2;
}

/** Approved AUTO-F3 default for template-import-mapping/v2 (CFG-IMP-001). */
export function defaultAutoF3MappingV2(): DeclarativeMappingV2 {
  return {
    schema: MAPPING_SCHEMA_V2,
    formula_cells: 'error',
    discovery: {
      scan_from_row: 1,
      scan_to_row: 120,
      allow_multiple_regions: true,
      max_consecutive_non_item_rows: 2,
      preceding_section_title_rows: 1,
      header_aliases: {
        item_code: ['№ п/п', '№'],
        item_label: [
          'Перечень требований к эксплуатации ПС',
          'Тесты',
          'Минимальный перечень',
          'Перечень',
          'Требование',
        ],
        normative_reference: ['№ п. Правил', 'Требования НТД*', 'Требования НТД'],
        pass: ['Соответствует', 'Норма', 'Имеется', 'Да'],
        fail: ['Не соответствует', 'Отклонение', 'Отсутствует', 'Нет'],
        not_applicable: ['Не требуется', 'Не применимо'],
        comment: ['Примечание', 'Примечания', 'Примечание (действителен до - )'],
      },
      item_code_modes: ['HIERARCHICAL_NUMERIC', 'INTEGER', 'GENERATED'],
    },
    assessment_tokens: {
      PASS: ['+', 'PASS'],
      FAIL: ['-', 'FAIL', F3_D8_TOKEN],
      NOT_APPLICABLE: ['N/A', 'NA', 'x', 'х'],
    },
    fail_evidence: {
      [F3_D8_TOKEN]: {
        assessment: 'FAIL',
        require: ['nonconformity_description', 'comment'],
      },
    },
    fields: [],
  };
}

/** Plain object body for createImportRevision (AUTO-F3 v2 bootstrap). */
export function buildAutoF3MappingV2Payload(): Record<string, unknown> {
  return JSON.parse(JSON.stringify(defaultAutoF3MappingV2())) as Record<string, unknown>;
}

export function parseTokenList(value: string): string[] {
  return value
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function formatTokenList(tokens: readonly string[]): string {
  return tokens.join(', ');
}

export function validateColumnLetter(column: string): boolean {
  return /^[A-Z]+$/.test(column);
}

export function validateGovernedSourceFamilyMapping(
  sourceFamily: string,
  mapping: Pick<DeclarativeMapping, 'assessment_tokens' | 'fail_evidence'>,
): MappingProblem[] {
  if (sourceFamily !== GOVERNED_SOURCE_FAMILY_F3) {
    return [];
  }
  const failures: MappingProblem[] = [];
  const pass = mapping.assessment_tokens.PASS;
  const fail = mapping.assessment_tokens.FAIL;
  const notApplicable = mapping.assessment_tokens.NOT_APPLICABLE;
  if (pass.includes(F3_D8_TOKEN)) {
    failures.push({
      code: 'F3_D8_TOKEN',
      message: 'Для семейства F3 значение «+-» нельзя сопоставить с PASS.',
    });
  }
  if (notApplicable.includes(F3_D8_TOKEN)) {
    failures.push({
      code: 'F3_D8_TOKEN',
      message: 'Для семейства F3 значение «+-» нельзя сопоставить с «не применяется».',
    });
  }
  const evidence = mapping.fail_evidence[F3_D8_TOKEN];
  const resolvesToFail = fail.includes(F3_D8_TOKEN) || evidence !== undefined;
  if (!resolvesToFail) {
    failures.push({
      code: 'F3_D8_TOKEN',
      message: 'Для семейства F3 значение «+-» должно соответствовать FAIL.',
    });
  }
  if (evidence === undefined) {
    failures.push({
      code: 'F3_D8_EVIDENCE',
      message:
        'Для семейства F3 значение «+-» требует доказательства: comment и nonconformity_description.',
    });
  } else {
    const required = new Set(evidence.require);
    if (!required.has('nonconformity_description') || !required.has('comment')) {
      failures.push({
        code: 'F3_D8_EVIDENCE',
        message:
          'Для семейства F3 значение «+-» должно требовать и comment, и nonconformity_description.',
      });
    }
  }
  return failures;
}

export function validateGovernedSourceFamilyCorrection(
  sourceFamily: string,
  overlay: CorrectionOverlay,
): MappingProblem[] {
  if (sourceFamily !== GOVERNED_SOURCE_FAMILY_F3) {
    return [];
  }
  const failures: MappingProblem[] = [];
  const tokens = overlay.assessment_tokens;
  if (tokens?.PASS?.includes(F3_D8_TOKEN)) {
    failures.push({
      code: 'F3_D8_TOKEN',
      message: 'Для семейства F3 значение «+-» нельзя сопоставить с PASS.',
    });
  }
  if (tokens?.NOT_APPLICABLE?.includes(F3_D8_TOKEN)) {
    failures.push({
      code: 'F3_D8_TOKEN',
      message: 'Для семейства F3 значение «+-» нельзя сопоставить с «не применяется».',
    });
  }
  if (overlay.fail_evidence !== undefined && overlay.fail_evidence[F3_D8_TOKEN] !== undefined) {
    const required = new Set(overlay.fail_evidence[F3_D8_TOKEN].require);
    if (!required.has('nonconformity_description') || !required.has('comment')) {
      failures.push({
        code: 'F3_D8_EVIDENCE',
        message:
          'Для семейства F3 значение «+-» должно требовать и comment, и nonconformity_description.',
      });
    }
  }
  return failures;
}

export function validateMapping(mapping: DeclarativeMapping): MappingProblem[] {
  const failures: MappingProblem[] = [];
  if (mapping.schema !== MAPPING_SCHEMA) {
    failures.push({ code: 'MAPPING_SCHEMA', message: `Схема должна быть ${MAPPING_SCHEMA}.` });
  }
  if (!Number.isInteger(mapping.header_row) || mapping.header_row < 1) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Строка заголовка должна быть целым числом ≥ 1.',
    });
  }
  if (!Number.isInteger(mapping.data_start_row) || mapping.data_start_row < 1) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Первая строка данных должна быть целым числом ≥ 1.',
    });
  }
  if (mapping.data_start_row <= mapping.header_row) {
    failures.push({
      code: 'MAPPING_ROW_ORDER',
      message: 'Первая строка данных должна быть ниже строки заголовка.',
    });
  }
  if (mapping.columns.item_code.kind !== 'column' && mapping.columns.item_code.kind !== 'fixed') {
    failures.push({
      code: 'MAPPING_BINDING',
      message: 'Код пункта должен быть привязан к колонке или фиксированному значению.',
    });
  }
  if (mapping.columns.item_label.kind !== 'column' && mapping.columns.item_label.kind !== 'fixed') {
    failures.push({
      code: 'MAPPING_BINDING',
      message: 'Название пункта должно быть привязано к колонке или фиксированному значению.',
    });
  }
  for (const name of COLUMN_NAMES) {
    const binding = mapping.columns[name];
    if (binding.kind === 'column' && !validateColumnLetter(binding.column)) {
      failures.push({
        code: 'MAPPING_BINDING',
        message: `Колонка «${name}» должна быть буквой A–Z.`,
      });
    }
    if (binding.kind === 'source_order' && name !== 'item_ordinal') {
      failures.push({
        code: 'MAPPING_BINDING',
        message: `Порядок источника допустим только для порядкового номера пункта.`,
      });
    }
  }
  failures.push(...validateSharedAssessmentAndFields(mapping));
  return failures;
}

export function validateMappingV2(mapping: DeclarativeMappingV2): MappingProblem[] {
  const failures: MappingProblem[] = [];
  if (mapping.schema !== MAPPING_SCHEMA_V2) {
    failures.push({
      code: 'MAPPING_SCHEMA',
      message: `Схема должна быть ${MAPPING_SCHEMA_V2}.`,
    });
  }
  if (mapping.formula_cells !== 'error' && mapping.formula_cells !== 'literal_formula_text') {
    failures.push({
      code: 'MAPPING_FORMULA_POLICY',
      message: 'Политика формул должна быть error или literal_formula_text.',
    });
  }
  failures.push(...validateDiscoveryForSave(mapping.discovery));
  failures.push(...validateSharedAssessmentAndFields(mapping));
  return failures;
}

export function validateMappingAny(mapping: AnyDeclarativeMapping): MappingProblem[] {
  return isDeclarativeMappingV2(mapping) ? validateMappingV2(mapping) : validateMapping(mapping);
}

export function mappingFromUnknown(
  value: unknown,
): { ok: true; mapping: AnyDeclarativeMapping } | { ok: false; failures: MappingProblem[] } {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return {
      ok: false,
      failures: [{ code: 'MAPPING_NOT_OBJECT', message: 'Сопоставление должно быть объектом.' }],
    };
  }
  const root = value as Record<string, unknown>;
  if (root.schema === MAPPING_SCHEMA_V2) {
    return mappingFromUnknownV2(root);
  }
  if (root.schema === MAPPING_SCHEMA) {
    return mappingFromUnknownV1(root);
  }
  return {
    ok: false,
    failures: [
      {
        code: root.schema === undefined ? 'MAPPING_SCHEMA' : 'UNSUPPORTED_MAPPING_SCHEMA',
        message:
          root.schema === undefined
            ? `Схема должна быть ${MAPPING_SCHEMA} или ${MAPPING_SCHEMA_V2}.`
            : `Схема сопоставления не поддерживается: ${String(root.schema)}.`,
      },
    ],
  };
}

function mappingFromUnknownV1(
  root: Record<string, unknown>,
): { ok: true; mapping: DeclarativeMapping } | { ok: false; failures: MappingProblem[] } {
  const allowed = [
    'schema',
    'header_row',
    'data_start_row',
    'formula_cells',
    'columns',
    'assessment_tokens',
    'fail_evidence',
    'fields',
  ];
  const failures: MappingProblem[] = [];
  for (const key of Object.keys(root)) {
    if (!allowed.includes(key)) {
      failures.push({
        code: 'MAPPING_UNKNOWN_MEMBER',
        message: `Неизвестный член сопоставления: ${key}.`,
      });
    }
  }
  if (root.schema !== MAPPING_SCHEMA) {
    failures.push({
      code: 'MAPPING_SCHEMA',
      message: `Схема должна быть ${MAPPING_SCHEMA}.`,
    });
  }
  const headerRow = requirePositiveInt(
    root.header_row,
    failures,
    'Строка заголовка должна быть целым числом ≥ 1.',
  );
  const dataStart = requirePositiveInt(
    root.data_start_row,
    failures,
    'Первая строка данных должна быть целым числом ≥ 1.',
  );
  if (headerRow !== undefined && dataStart !== undefined && dataStart <= headerRow) {
    failures.push({
      code: 'MAPPING_ROW_ORDER',
      message: 'Первая строка данных должна быть ниже строки заголовка.',
    });
  }
  let formulaCells: FormulaCellPolicy = 'error';
  if (root.formula_cells === undefined || root.formula_cells === 'error') {
    formulaCells = 'error';
  } else if (root.formula_cells === 'literal_formula_text') {
    formulaCells = 'literal_formula_text';
  } else {
    failures.push({
      code: 'MAPPING_FORMULA_POLICY',
      message: 'Политика формул должна быть error или literal_formula_text.',
    });
  }
  const columns = parseColumns(root.columns, failures);
  const tokens = parseTokens(root.assessment_tokens, failures);
  const failEvidence = parseFailEvidence(root.fail_evidence, failures);
  const fields = parseFields(root.fields, failures);
  if (failures.length > 0 || columns === undefined || tokens === undefined) {
    return { ok: false, failures };
  }
  return {
    ok: true,
    mapping: {
      schema: MAPPING_SCHEMA,
      header_row: headerRow ?? 1,
      data_start_row: dataStart ?? 2,
      formula_cells: formulaCells,
      columns,
      assessment_tokens: tokens,
      fail_evidence: failEvidence,
      fields,
    },
  };
}

function mappingFromUnknownV2(
  root: Record<string, unknown>,
): { ok: true; mapping: DeclarativeMappingV2 } | { ok: false; failures: MappingProblem[] } {
  const allowed = [
    'schema',
    'formula_cells',
    'discovery',
    'assessment_tokens',
    'fail_evidence',
    'fields',
  ];
  const failures: MappingProblem[] = [];
  for (const key of Object.keys(root)) {
    if (!allowed.includes(key)) {
      failures.push({
        code: 'MAPPING_UNKNOWN_MEMBER',
        message: `Неизвестный член сопоставления: ${key}.`,
      });
    }
  }
  if (root.schema !== MAPPING_SCHEMA_V2) {
    failures.push({
      code: 'UNSUPPORTED_MAPPING_SCHEMA',
      message: `Схема должна быть ${MAPPING_SCHEMA_V2}.`,
    });
  }
  for (const required of [
    'schema',
    'formula_cells',
    'discovery',
    'assessment_tokens',
    'fail_evidence',
    'fields',
  ] as const) {
    if (root[required] === undefined) {
      failures.push({
        code: 'MAPPING_REQUIRED_MEMBER',
        message: `Член сопоставления «${required}» обязателен.`,
      });
    }
  }

  let formulaCells: FormulaCellPolicy | undefined;
  if (root.formula_cells === 'error' || root.formula_cells === 'literal_formula_text') {
    formulaCells = root.formula_cells;
  } else if (root.formula_cells !== undefined) {
    failures.push({
      code: 'MAPPING_FORMULA_POLICY',
      message: 'Политика формул должна быть error или literal_formula_text.',
    });
  }

  const discovery =
    root.discovery === undefined ? undefined : parseDiscoveryV2(root.discovery, failures);
  const tokens =
    root.assessment_tokens === undefined
      ? undefined
      : parseTokensStrictV2(root.assessment_tokens, failures);
  const failEvidence =
    root.fail_evidence === undefined
      ? undefined
      : parseFailEvidenceStrictV2(root.fail_evidence, failures);
  const fields = root.fields === undefined ? undefined : parseFieldsStrictV2(root.fields, failures);

  if (
    failures.length > 0 ||
    discovery === undefined ||
    tokens === undefined ||
    failEvidence === undefined ||
    fields === undefined ||
    formulaCells === undefined
  ) {
    return { ok: false, failures };
  }
  return {
    ok: true,
    mapping: {
      schema: MAPPING_SCHEMA_V2,
      formula_cells: formulaCells,
      discovery,
      assessment_tokens: tokens,
      fail_evidence: failEvidence,
      fields,
    },
  };
}

export function buildMappingPayload(mapping: DeclarativeMapping): DeclarativeMapping;
export function buildMappingPayload(mapping: DeclarativeMappingV2): DeclarativeMappingV2;
export function buildMappingPayload(mapping: AnyDeclarativeMapping): AnyDeclarativeMapping;
export function buildMappingPayload(mapping: AnyDeclarativeMapping): AnyDeclarativeMapping {
  if (isDeclarativeMappingV2(mapping)) {
    return buildMappingPayloadV2(mapping);
  }
  return buildMappingPayloadV1(mapping);
}

export function buildMappingPayloadAny(mapping: AnyDeclarativeMapping): AnyDeclarativeMapping {
  return buildMappingPayload(mapping);
}

function buildMappingPayloadV1(mapping: DeclarativeMapping): DeclarativeMapping {
  return {
    schema: MAPPING_SCHEMA,
    header_row: mapping.header_row,
    data_start_row: mapping.data_start_row,
    formula_cells: mapping.formula_cells,
    columns: {
      section_code: cloneBinding(mapping.columns.section_code),
      section_title: cloneBinding(mapping.columns.section_title),
      item_code: cloneBinding(mapping.columns.item_code),
      item_label: cloneBinding(mapping.columns.item_label),
      item_ordinal: cloneBinding(mapping.columns.item_ordinal),
      assessment_token: cloneBinding(mapping.columns.assessment_token),
    },
    assessment_tokens: {
      PASS: [...mapping.assessment_tokens.PASS],
      FAIL: [...mapping.assessment_tokens.FAIL],
      NOT_APPLICABLE: [...mapping.assessment_tokens.NOT_APPLICABLE],
    },
    fail_evidence: Object.fromEntries(
      Object.entries(mapping.fail_evidence).map(([token, rule]) => [
        token,
        { assessment: 'FAIL' as const, require: [...rule.require] },
      ]),
    ),
    fields: mapping.fields.map(cloneField),
  };
}

function buildMappingPayloadV2(mapping: DeclarativeMappingV2): DeclarativeMappingV2 {
  return {
    schema: MAPPING_SCHEMA_V2,
    formula_cells: mapping.formula_cells,
    discovery: {
      scan_from_row: mapping.discovery.scan_from_row,
      scan_to_row: mapping.discovery.scan_to_row,
      allow_multiple_regions: mapping.discovery.allow_multiple_regions,
      max_consecutive_non_item_rows: mapping.discovery.max_consecutive_non_item_rows,
      preceding_section_title_rows: mapping.discovery.preceding_section_title_rows,
      header_aliases: {
        item_code: [...mapping.discovery.header_aliases.item_code],
        item_label: [...mapping.discovery.header_aliases.item_label],
        normative_reference: [...mapping.discovery.header_aliases.normative_reference],
        pass: [...mapping.discovery.header_aliases.pass],
        fail: [...mapping.discovery.header_aliases.fail],
        not_applicable: [...mapping.discovery.header_aliases.not_applicable],
        comment: [...mapping.discovery.header_aliases.comment],
      },
      item_code_modes: [...mapping.discovery.item_code_modes],
    },
    assessment_tokens: {
      PASS: [...mapping.assessment_tokens.PASS],
      FAIL: [...mapping.assessment_tokens.FAIL],
      NOT_APPLICABLE: [...mapping.assessment_tokens.NOT_APPLICABLE],
    },
    fail_evidence: Object.fromEntries(
      Object.entries(mapping.fail_evidence).map(([token, rule]) => [
        token,
        { assessment: 'FAIL' as const, require: [...rule.require] },
      ]),
    ),
    fields: mapping.fields.map(cloneField),
  };
}

export interface CorrectionDraft {
  readonly includeAssessmentTokens: boolean;
  readonly assessmentTokens: {
    readonly PASS: string;
    readonly FAIL: string;
    readonly NOT_APPLICABLE: string;
  };
  readonly failEvidenceRows: readonly {
    readonly token: string;
    readonly requireComment: boolean;
    readonly requireNonconformity: boolean;
  }[];
  readonly columnOverrides: readonly {
    readonly name: ColumnName;
    readonly enabled: boolean;
    readonly binding: ColumnBinding;
  }[];
  readonly includeFields: boolean;
  readonly fields: readonly FieldMapping[];
  readonly itemCodeRewrites: readonly ItemCodeRewrite[];
  readonly formulaCells: '' | FormulaCellPolicy;
}

export function emptyCorrectionDraft(): CorrectionDraft {
  return {
    includeAssessmentTokens: false,
    assessmentTokens: { PASS: '', FAIL: '', NOT_APPLICABLE: '' },
    failEvidenceRows: [],
    columnOverrides: COLUMN_NAMES.map((name) => ({
      name,
      enabled: false,
      binding: name === 'item_ordinal' ? { kind: 'source_order' } : { kind: 'absent' },
    })),
    includeFields: false,
    fields: [],
    itemCodeRewrites: [],
    formulaCells: '',
  };
}

export function buildCorrectionPayload(
  draft: CorrectionDraft,
):
  | { ok: true; correction: { schema: typeof CORRECTION_SCHEMA; overlay: CorrectionOverlay } }
  | { ok: false; failures: MappingProblem[] } {
  const failures: MappingProblem[] = [];
  const overlay: {
    assessment_tokens?: CorrectionOverlay['assessment_tokens'];
    fail_evidence?: DeclarativeMapping['fail_evidence'];
    columns?: Partial<DeclarativeMapping['columns']>;
    fields?: FieldMapping[];
    item_code_rewrites?: ItemCodeRewrite[];
    formula_cells?: FormulaCellPolicy;
  } = {};

  if (draft.includeAssessmentTokens) {
    const tokens: {
      PASS?: string[];
      FAIL?: string[];
      NOT_APPLICABLE?: string[];
    } = {};
    for (const assessment of ASSESSMENTS) {
      const listed = parseTokenList(draft.assessmentTokens[assessment]);
      if (listed.length > 0) {
        tokens[assessment] = listed;
      }
    }
    if (Object.keys(tokens).length === 0) {
      failures.push({
        code: 'CORRECTION_TOKENS',
        message: 'Укажите хотя бы один токен оценки или снимите этот блок.',
      });
    } else {
      overlay.assessment_tokens = tokens;
    }
  }

  if (draft.failEvidenceRows.length > 0) {
    const evidence: Record<string, FailEvidenceRule> = {};
    draft.failEvidenceRows.forEach((row, index) => {
      if (row.token.trim().length < 1) {
        failures.push({
          code: 'CORRECTION_FAIL_EVIDENCE',
          message: `Доказательство ${index + 1}: укажите исходный токен.`,
        });
        return;
      }
      const require: FailEvidenceRule['require'] = [
        ...(row.requireNonconformity ? (['nonconformity_description'] as const) : []),
        ...(row.requireComment ? (['comment'] as const) : []),
      ];
      if (require.length === 0) {
        failures.push({
          code: 'CORRECTION_FAIL_EVIDENCE',
          message: `Доказательство ${index + 1}: выберите хотя бы одно обязательное поле.`,
        });
        return;
      }
      evidence[row.token.trim()] = { assessment: 'FAIL', require };
    });
    if (Object.keys(evidence).length > 0) {
      overlay.fail_evidence = evidence;
    }
  }

  const columns: Partial<DeclarativeMapping['columns']> = {};
  for (const row of draft.columnOverrides) {
    if (!row.enabled) {
      continue;
    }
    if (row.binding.kind === 'column' && !validateColumnLetter(row.binding.column)) {
      failures.push({
        code: 'CORRECTION_COLUMNS',
        message: `Колонка «${row.name}»: укажите букву A–Z.`,
      });
      continue;
    }
    if (row.binding.kind === 'source_order' && row.name !== 'item_ordinal') {
      failures.push({
        code: 'CORRECTION_COLUMNS',
        message: 'Порядок источника допустим только для порядкового номера пункта.',
      });
      continue;
    }
    columns[row.name] = cloneBinding(row.binding);
  }
  if (Object.keys(columns).length > 0) {
    overlay.columns = columns;
  }

  if (draft.includeFields) {
    overlay.fields = draft.fields.map(cloneField);
    draft.fields.forEach((field, index) => {
      if (field.field_key.trim().length < 1 || field.label.trim().length < 1) {
        failures.push({
          code: 'CORRECTION_FIELDS',
          message: `Поле ${index + 1}: укажите ключ и подпись.`,
        });
      }
    });
  }

  if (draft.itemCodeRewrites.length > 0) {
    const rewrites: ItemCodeRewrite[] = [];
    draft.itemCodeRewrites.forEach((row, index) => {
      if (row.from.trim().length < 1 || row.to.trim().length < 1) {
        failures.push({
          code: 'CORRECTION_ITEM_CODE_REWRITES',
          message: `Переименование ${index + 1}: заполните исходный и новый код.`,
        });
        return;
      }
      rewrites.push({ from: row.from.trim(), to: row.to.trim() });
    });
    if (rewrites.length > 0) {
      overlay.item_code_rewrites = rewrites;
    }
  }

  if (draft.formulaCells === 'error' || draft.formulaCells === 'literal_formula_text') {
    overlay.formula_cells = draft.formulaCells;
  }

  if (Object.keys(overlay).length === 0) {
    failures.push({
      code: 'CORRECTION_OVERLAY',
      message: 'Укажите хотя бы одно изменение коррекции.',
    });
  }

  if (failures.length > 0) {
    return { ok: false, failures };
  }
  return {
    ok: true,
    correction: {
      schema: CORRECTION_SCHEMA,
      overlay,
    },
  };
}

export function emptyField(): FieldMapping {
  return {
    field_key: '',
    binding_kind: 'CHECKLIST_DYNAMIC',
    label: '',
    value_type: 'TEXT',
    owner_code: 'INSPECTOR',
    required_rule: emptyRuleObject(),
    applicability_rule: emptyRuleObject(),
    validation: emptyRuleObject(),
    placement: 'RESULT_BLOCK',
    pdf_metadata: emptyRuleObject(),
    snapshot_path: null,
  };
}

export function replaceColumn(
  mapping: DeclarativeMapping,
  name: ColumnName,
  binding: ColumnBinding,
): DeclarativeMapping {
  return {
    ...mapping,
    columns: {
      ...mapping.columns,
      [name]: binding,
    },
  };
}

export function replaceTokens(
  mapping: DeclarativeMapping,
  assessment: CanonicalAssessment,
  tokens: readonly string[],
): DeclarativeMapping {
  return {
    ...mapping,
    assessment_tokens: {
      ...mapping.assessment_tokens,
      [assessment]: tokens,
    },
  };
}

function cloneBinding(binding: ColumnBinding): ColumnBinding {
  if (binding.kind === 'column') {
    return { kind: 'column', column: binding.column };
  }
  if (binding.kind === 'fixed') {
    return { kind: 'fixed', value: binding.value };
  }
  if (binding.kind === 'source_order') {
    return { kind: 'source_order' };
  }
  return { kind: 'absent' };
}

function cloneField(field: FieldMapping): FieldMapping {
  return {
    field_key: field.field_key,
    binding_kind: field.binding_kind,
    label: field.label,
    value_type: field.value_type,
    owner_code: field.owner_code,
    required_rule: field.required_rule,
    applicability_rule: field.applicability_rule,
    validation: field.validation,
    placement: field.placement,
    pdf_metadata: field.pdf_metadata,
    snapshot_path: field.snapshot_path,
    ...(field.presentation_only === true ? { presentation_only: true as const } : {}),
  };
}

function validateSharedAssessmentAndFields(
  mapping: Pick<DeclarativeMapping, 'assessment_tokens' | 'fields'>,
): MappingProblem[] {
  const failures: MappingProblem[] = [];
  const seen = new Map<string, CanonicalAssessment>();
  for (const assessment of ASSESSMENTS) {
    for (const token of mapping.assessment_tokens[assessment]) {
      const key = token.trim();
      const previous = seen.get(key);
      if (previous !== undefined && previous !== assessment) {
        failures.push({
          code: 'AMBIGUOUS_ASSESSMENT_TOKEN',
          message: `Токен «${token}» сопоставлен и с ${previous}, и с ${assessment}.`,
        });
      }
      seen.set(key, assessment);
    }
  }
  mapping.fields.forEach((field, index) => {
    if (field.field_key.trim().length < 1) {
      failures.push({
        code: 'MAPPING_FIELD_KEY',
        message: `Поле ${index + 1}: укажите ключ поля.`,
      });
    }
    if (field.label.trim().length < 1) {
      failures.push({
        code: 'MAPPING_FIELD_LABEL',
        message: `Поле ${index + 1}: укажите подпись.`,
      });
    }
    if (
      field.binding_kind === 'CASE_SNAPSHOT' &&
      (field.snapshot_path === null || field.snapshot_path === '')
    ) {
      failures.push({
        code: 'SNAPSHOT_PATH_XOR',
        message: `Поле ${index + 1}: CASE_SNAPSHOT требует путь снимка.`,
      });
    }
    if (field.binding_kind === 'CHECKLIST_DYNAMIC' && field.snapshot_path !== null) {
      failures.push({
        code: 'SNAPSHOT_PATH_XOR',
        message: `Поле ${index + 1}: CHECKLIST_DYNAMIC требует пустой путь снимка.`,
      });
    }
  });
  return failures;
}

function validateDiscoveryForSave(discovery: DiscoveryGrammarV2): MappingProblem[] {
  const failures: MappingProblem[] = [];
  if (!Number.isInteger(discovery.scan_from_row) || discovery.scan_from_row < 1) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Строка начала сканирования должна быть целым числом ≥ 1.',
    });
  }
  if (!Number.isInteger(discovery.scan_to_row) || discovery.scan_to_row < 1) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Строка конца сканирования должна быть целым числом ≥ 1.',
    });
  }
  if (discovery.scan_to_row < discovery.scan_from_row) {
    failures.push({
      code: 'MAPPING_ROW_ORDER',
      message: 'Строка конца сканирования должна быть не раньше начала.',
    });
  }
  if (
    !Number.isInteger(discovery.max_consecutive_non_item_rows) ||
    discovery.max_consecutive_non_item_rows < 1 ||
    discovery.max_consecutive_non_item_rows > 10
  ) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Максимум пустых строк подряд должен быть целым числом от 1 до 10.',
    });
  }
  if (
    !Number.isInteger(discovery.preceding_section_title_rows) ||
    discovery.preceding_section_title_rows < 0 ||
    discovery.preceding_section_title_rows > 3
  ) {
    failures.push({
      code: 'MAPPING_INTEGER',
      message: 'Число строк заголовка раздела должно быть целым от 0 до 3.',
    });
  }
  if (discovery.header_aliases.item_label.length < 1) {
    failures.push({
      code: 'MAPPING_HEADER_ALIASES',
      message: 'Укажите хотя бы один псевдоним для названия пункта.',
    });
  }
  if (discovery.item_code_modes.length < 1) {
    failures.push({
      code: 'MAPPING_ITEM_CODE_MODES',
      message: 'Выберите хотя бы один режим кода пункта.',
    });
  }
  for (const mode of discovery.item_code_modes) {
    if (!(ITEM_CODE_MODES_V2 as readonly string[]).includes(mode)) {
      failures.push({
        code: 'MAPPING_ITEM_CODE_MODES',
        message: `Режим кода пункта не поддержан: ${mode}.`,
      });
    }
  }
  return failures;
}

function parseDiscoveryV2(
  value: unknown,
  failures: MappingProblem[],
): DiscoveryGrammarV2 | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({ code: 'MAPPING_DISCOVERY', message: 'Блок discovery должен быть объектом.' });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  const allowed = [
    'scan_from_row',
    'scan_to_row',
    'allow_multiple_regions',
    'max_consecutive_non_item_rows',
    'preceding_section_title_rows',
    'header_aliases',
    'item_code_modes',
  ];
  for (const key of Object.keys(root)) {
    if (!allowed.includes(key)) {
      failures.push({
        code: 'MAPPING_UNKNOWN_MEMBER',
        message: `Неизвестный член discovery: ${key}.`,
      });
    }
  }
  const scanFrom = requirePositiveInt(
    root.scan_from_row,
    failures,
    'Строка начала сканирования должна быть целым числом ≥ 1.',
  );
  const scanTo = requirePositiveInt(
    root.scan_to_row,
    failures,
    'Строка конца сканирования должна быть целым числом ≥ 1.',
  );
  if (scanFrom !== undefined && scanTo !== undefined && scanTo < scanFrom) {
    failures.push({
      code: 'MAPPING_ROW_ORDER',
      message: 'Строка конца сканирования должна быть не раньше начала.',
    });
  }
  if (typeof root.allow_multiple_regions !== 'boolean') {
    failures.push({
      code: 'MAPPING_DISCOVERY',
      message: 'allow_multiple_regions должен быть логическим значением.',
    });
  }
  const maxNonItem = requireBoundedInt(
    root.max_consecutive_non_item_rows,
    failures,
    1,
    10,
    'Максимум пустых строк подряд должен быть целым числом от 1 до 10.',
  );
  const preceding = requireBoundedInt(
    root.preceding_section_title_rows,
    failures,
    0,
    3,
    'Число строк заголовка раздела должно быть целым от 0 до 3.',
  );
  const aliases = parseHeaderAliasesV2(root.header_aliases, failures);
  const modes = parseItemCodeModesV2(root.item_code_modes, failures);
  if (
    scanFrom === undefined ||
    scanTo === undefined ||
    maxNonItem === undefined ||
    preceding === undefined ||
    aliases === undefined ||
    modes === undefined ||
    typeof root.allow_multiple_regions !== 'boolean'
  ) {
    return undefined;
  }
  return {
    scan_from_row: scanFrom,
    scan_to_row: scanTo,
    allow_multiple_regions: root.allow_multiple_regions,
    max_consecutive_non_item_rows: maxNonItem,
    preceding_section_title_rows: preceding,
    header_aliases: aliases,
    item_code_modes: modes,
  };
}

function parseHeaderAliasesV2(
  value: unknown,
  failures: MappingProblem[],
): HeaderAliasesV2 | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({
      code: 'MAPPING_HEADER_ALIASES',
      message: 'Псевдонимы заголовков должны быть объектом.',
    });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  for (const key of Object.keys(root)) {
    if (!(HEADER_ALIAS_KEYS_V2 as readonly string[]).includes(key)) {
      failures.push({
        code: 'MAPPING_UNKNOWN_MEMBER',
        message: `Неизвестный псевдоним заголовка: ${key}.`,
      });
    }
  }
  const result: Partial<Record<(typeof HEADER_ALIAS_KEYS_V2)[number], string[]>> = {};
  for (const key of HEADER_ALIAS_KEYS_V2) {
    const listed = root[key];
    if (listed === undefined) {
      result[key] = [];
      continue;
    }
    if (
      !Array.isArray(listed) ||
      listed.some((item) => typeof item !== 'string' || item.trim().length < 1)
    ) {
      failures.push({
        code: 'MAPPING_HEADER_ALIASES',
        message: `Псевдонимы «${key}» должны быть непустым списком строк.`,
      });
      continue;
    }
    result[key] = listed as string[];
  }
  if ((result.item_label ?? []).length < 1) {
    failures.push({
      code: 'MAPPING_HEADER_ALIASES',
      message: 'Укажите хотя бы один псевдоним для названия пункта.',
    });
    return undefined;
  }
  return {
    item_code: result.item_code ?? [],
    item_label: result.item_label ?? [],
    normative_reference: result.normative_reference ?? [],
    pass: result.pass ?? [],
    fail: result.fail ?? [],
    not_applicable: result.not_applicable ?? [],
    comment: result.comment ?? [],
  };
}

function parseItemCodeModesV2(
  value: unknown,
  failures: MappingProblem[],
): ItemCodeModeV2[] | undefined {
  if (!Array.isArray(value) || value.length < 1) {
    failures.push({
      code: 'MAPPING_ITEM_CODE_MODES',
      message: 'Режимы кода пункта должны быть непустым списком.',
    });
    return undefined;
  }
  const modes: ItemCodeModeV2[] = [];
  for (const item of value) {
    if (!(ITEM_CODE_MODES_V2 as readonly string[]).includes(item as string)) {
      failures.push({
        code: 'MAPPING_ITEM_CODE_MODES',
        message: `Режим кода пункта не поддержан: ${String(item)}.`,
      });
      continue;
    }
    modes.push(item as ItemCodeModeV2);
  }
  return modes.length > 0 ? modes : undefined;
}

const V2_ASSESSMENT_TOKEN_KEYS = ['PASS', 'FAIL', 'NOT_APPLICABLE'] as const;
const V2_FIELD_MAPPING_MEMBERS = [
  'field_key',
  'binding_kind',
  'label',
  'value_type',
  'owner_code',
  'required_rule',
  'applicability_rule',
  'validation',
  'placement',
  'pdf_metadata',
  'snapshot_path',
  'presentation_only',
] as const;

function rejectUnknownObjectMembers(
  root: Record<string, unknown>,
  allowed: readonly string[],
  failures: MappingProblem[],
  prefix: string,
): void {
  for (const key of Object.keys(root)) {
    if (!allowed.includes(key)) {
      failures.push({
        code: 'MAPPING_UNKNOWN_MEMBER',
        message: `${prefix}: неизвестный член «${key}».`,
      });
    }
  }
}

function parseTokensStrictV2(
  value: unknown,
  failures: MappingProblem[],
): DeclarativeMapping['assessment_tokens'] | undefined {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    rejectUnknownObjectMembers(
      value as Record<string, unknown>,
      V2_ASSESSMENT_TOKEN_KEYS,
      failures,
      'assessment_tokens',
    );
  }
  return parseTokens(value, failures);
}

function parseFailEvidenceStrictV2(
  value: unknown,
  failures: MappingProblem[],
): DeclarativeMapping['fail_evidence'] | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({
      code: 'MAPPING_FAIL_EVIDENCE',
      message: 'Доказательства FAIL должны быть объектом.',
    });
    return undefined;
  }
  for (const [token, rule] of Object.entries(value as Record<string, unknown>)) {
    if (rule !== null && typeof rule === 'object' && !Array.isArray(rule)) {
      rejectUnknownObjectMembers(
        rule as Record<string, unknown>,
        ['assessment', 'require'],
        failures,
        `fail_evidence.${token}`,
      );
    }
  }
  return parseFailEvidence(value, failures);
}

function parseFieldsStrictV2(
  value: unknown,
  failures: MappingProblem[],
): FieldMapping[] | undefined {
  if (!Array.isArray(value)) {
    failures.push({ code: 'MAPPING_FIELDS', message: 'Поля должны быть списком.' });
    return undefined;
  }
  value.forEach((item, index) => {
    if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
      rejectUnknownObjectMembers(
        item as Record<string, unknown>,
        V2_FIELD_MAPPING_MEMBERS,
        failures,
        `fields[${index}]`,
      );
    }
  });
  return parseFields(value, failures);
}

function requirePositiveInt(
  value: unknown,
  failures: MappingProblem[],
  message: string,
): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    failures.push({ code: 'MAPPING_INTEGER', message });
    return undefined;
  }
  return value;
}

function requireBoundedInt(
  value: unknown,
  failures: MappingProblem[],
  min: number,
  max: number,
  message: string,
): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
    failures.push({ code: 'MAPPING_INTEGER', message });
    return undefined;
  }
  return value;
}

function parseColumns(
  value: unknown,
  failures: MappingProblem[],
): DeclarativeMapping['columns'] | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({ code: 'MAPPING_COLUMNS', message: 'Колонки должны быть объектом.' });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  const result: {
    section_code?: ColumnBinding;
    section_title?: ColumnBinding;
    item_code?: ColumnBinding;
    item_label?: ColumnBinding;
    item_ordinal?: ColumnBinding;
    assessment_token?: ColumnBinding;
  } = {};
  for (const name of COLUMN_NAMES) {
    const binding = parseBinding(root[name], name === 'item_ordinal', name, failures);
    if (binding !== undefined) {
      result[name] = binding;
    }
  }
  if (result.item_code === undefined || result.item_label === undefined) {
    return undefined;
  }
  return {
    section_code: result.section_code ?? { kind: 'absent' },
    section_title: result.section_title ?? { kind: 'absent' },
    item_code: result.item_code,
    item_label: result.item_label,
    item_ordinal: result.item_ordinal ?? { kind: 'source_order' },
    assessment_token: result.assessment_token ?? { kind: 'absent' },
  };
}

function parseBinding(
  value: unknown,
  allowSourceOrder: boolean,
  name: string,
  failures: MappingProblem[],
): ColumnBinding | undefined {
  if (value === undefined) {
    return { kind: allowSourceOrder ? 'source_order' : 'absent' };
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({
      code: 'MAPPING_BINDING',
      message: `Привязка «${name}» должна быть объектом.`,
    });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  if (
    root.kind === 'column' &&
    typeof root.column === 'string' &&
    validateColumnLetter(root.column)
  ) {
    return { kind: 'column', column: root.column };
  }
  if (root.kind === 'fixed' && typeof root.value === 'string') {
    return { kind: 'fixed', value: root.value };
  }
  if (root.kind === 'absent') {
    return { kind: 'absent' };
  }
  if (allowSourceOrder && root.kind === 'source_order') {
    return { kind: 'source_order' };
  }
  failures.push({ code: 'MAPPING_BINDING', message: `Привязка «${name}» не поддержана.` });
  return undefined;
}

function parseTokens(
  value: unknown,
  failures: MappingProblem[],
): DeclarativeMapping['assessment_tokens'] | undefined {
  if (value === undefined) {
    return { PASS: [], FAIL: [], NOT_APPLICABLE: [] };
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({ code: 'MAPPING_TOKENS', message: 'Токены оценки должны быть объектом.' });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  const result: Record<CanonicalAssessment, string[]> = {
    PASS: [],
    FAIL: [],
    NOT_APPLICABLE: [],
  };
  for (const assessment of ASSESSMENTS) {
    const listed = root[assessment];
    if (listed === undefined) {
      continue;
    }
    if (
      !Array.isArray(listed) ||
      listed.some((item) => typeof item !== 'string' || item.length < 1)
    ) {
      failures.push({
        code: 'MAPPING_TOKENS',
        message: `Токены ${assessment} должны быть непустым списком строк.`,
      });
      continue;
    }
    result[assessment] = listed as string[];
  }
  const seen = new Map<string, CanonicalAssessment>();
  for (const assessment of ASSESSMENTS) {
    for (const token of result[assessment]) {
      const key = token.trim();
      const previous = seen.get(key);
      if (previous !== undefined && previous !== assessment) {
        failures.push({
          code: 'AMBIGUOUS_ASSESSMENT_TOKEN',
          message: `Токен «${token}» сопоставлен и с ${previous}, и с ${assessment}.`,
        });
      }
      seen.set(key, assessment);
    }
  }
  return result;
}

function parseFailEvidence(
  value: unknown,
  failures: MappingProblem[],
): DeclarativeMapping['fail_evidence'] {
  if (value === undefined) {
    return {};
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({
      code: 'MAPPING_FAIL_EVIDENCE',
      message: 'Доказательства FAIL должны быть объектом.',
    });
    return {};
  }
  const result: Record<string, FailEvidenceRule> = {};
  for (const [token, rule] of Object.entries(value as Record<string, unknown>)) {
    if (rule === null || typeof rule !== 'object' || Array.isArray(rule)) {
      failures.push({
        code: 'MAPPING_FAIL_EVIDENCE',
        message: `Доказательство «${token}» должно быть объектом.`,
      });
      continue;
    }
    const row = rule as Record<string, unknown>;
    if (row.assessment !== 'FAIL') {
      failures.push({
        code: 'MAPPING_FAIL_EVIDENCE',
        message: `Доказательство «${token}» может относиться только к FAIL.`,
      });
      continue;
    }
    if (
      !Array.isArray(row.require) ||
      row.require.some((item) => item !== 'nonconformity_description' && item !== 'comment')
    ) {
      failures.push({
        code: 'MAPPING_FAIL_EVIDENCE',
        message: `Доказательство «${token}»: недопустимый набор полей.`,
      });
      continue;
    }
    result[token] = {
      assessment: 'FAIL',
      require: row.require as FailEvidenceRule['require'],
    };
  }
  return result;
}

function parseFields(value: unknown, failures: MappingProblem[]): FieldMapping[] {
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    failures.push({ code: 'MAPPING_FIELDS', message: 'Поля должны быть списком.' });
    return [];
  }
  const fields: FieldMapping[] = [];
  value.forEach((item, index) => {
    const parsed = parseField(item, index, failures);
    if (parsed !== undefined) {
      fields.push(parsed);
    }
  });
  return fields;
}

function parseField(
  value: unknown,
  index: number,
  failures: MappingProblem[],
): FieldMapping | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    failures.push({ code: 'MAPPING_FIELD', message: `Поле ${index + 1} должно быть объектом.` });
    return undefined;
  }
  const root = value as Record<string, unknown>;
  const fieldKey = typeof root.field_key === 'string' ? root.field_key : '';
  const bindingKind = root.binding_kind;
  const valueType = root.value_type;
  const owner = root.owner_code;
  const placement = root.placement;
  if (fieldKey.length < 1) {
    failures.push({
      code: 'MAPPING_FIELD_KEY',
      message: `Поле ${index + 1}: укажите ключ поля.`,
    });
  }
  if (!BINDING_KINDS.includes(bindingKind as FieldMapping['binding_kind'])) {
    failures.push({
      code: 'INVALID_BINDING_KIND',
      message: `Поле ${index + 1}: вид привязки не поддержан.`,
    });
  }
  if (!VALUE_TYPES.includes(valueType as FieldMapping['value_type'])) {
    failures.push({
      code: 'UNSUPPORTED_VALUE_TYPE',
      message: `Поле ${index + 1}: тип значения не поддержан.`,
    });
  }
  if (!OWNER_CODES.includes(owner as FieldMapping['owner_code'])) {
    failures.push({
      code: 'UNSUPPORTED_OWNER',
      message: `Поле ${index + 1}: владелец не поддержан.`,
    });
  }
  if (!PLACEMENTS.includes(placement as FieldMapping['placement'])) {
    failures.push({
      code: 'UNSUPPORTED_PLACEMENT',
      message: `Поле ${index + 1}: размещение не поддержано.`,
    });
  }
  const snapshotPath =
    root.snapshot_path === undefined || root.snapshot_path === null
      ? null
      : typeof root.snapshot_path === 'string'
        ? root.snapshot_path
        : undefined;
  if (snapshotPath === undefined) {
    failures.push({
      code: 'INVALID_SNAPSHOT_PATH',
      message: `Поле ${index + 1}: путь снимка должен быть строкой или пустым.`,
    });
  }
  if (bindingKind === 'CASE_SNAPSHOT' && (snapshotPath === null || snapshotPath === '')) {
    failures.push({
      code: 'SNAPSHOT_PATH_XOR',
      message: `Поле ${index + 1}: CASE_SNAPSHOT требует путь снимка.`,
    });
  }
  if (bindingKind === 'CHECKLIST_DYNAMIC' && snapshotPath !== null) {
    failures.push({
      code: 'SNAPSHOT_PATH_XOR',
      message: `Поле ${index + 1}: CHECKLIST_DYNAMIC требует пустой путь снимка.`,
    });
  }
  if (
    !isPlainObject(root.required_rule) ||
    !isPlainObject(root.applicability_rule) ||
    !isPlainObject(root.validation)
  ) {
    failures.push({
      code: 'INVALID_FIELD_RULE',
      message: `Поле ${index + 1}: декларативные правила должны быть объектами.`,
    });
    return undefined;
  }
  if (typeof root.label !== 'string' || root.label.length < 1) {
    failures.push({
      code: 'MAPPING_FIELD_LABEL',
      message: `Поле ${index + 1}: укажите подпись.`,
    });
    return undefined;
  }
  if (
    !BINDING_KINDS.includes(bindingKind as FieldMapping['binding_kind']) ||
    !VALUE_TYPES.includes(valueType as FieldMapping['value_type']) ||
    !OWNER_CODES.includes(owner as FieldMapping['owner_code']) ||
    !PLACEMENTS.includes(placement as FieldMapping['placement']) ||
    snapshotPath === undefined
  ) {
    return undefined;
  }
  return {
    field_key: fieldKey,
    binding_kind: bindingKind as FieldMapping['binding_kind'],
    label: root.label,
    value_type: valueType as FieldMapping['value_type'],
    owner_code: owner as FieldMapping['owner_code'],
    required_rule: root.required_rule,
    applicability_rule: root.applicability_rule,
    validation: root.validation,
    placement: placement as FieldMapping['placement'],
    pdf_metadata: isPlainObject(root.pdf_metadata) ? root.pdf_metadata : { ...EMPTY_OBJECT },
    snapshot_path: snapshotPath,
    ...(root.presentation_only === true ? { presentation_only: true as const } : {}),
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
